import { useState, useEffect, FC } from "react";
import {
  Box,
  Paper,
  FormControlLabel,
  Typography,
  Divider,
  Button,
  Switch,
  Avatar,
  useTheme,
} from "@mui/material";
import __ from "src/utils/Translations";
import { AxiosError } from "axios";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { EditStructureForm } from "src/Pages/Company/OrganizationStructure/Forms/EditStructureForm/EditStructureForm";
import useChangeVisibility from "src/Pages/Company/OrganizationStructure/api/useChangeVisibility";
import useFetchStructureData from "src/Pages/Company/OrganizationStructure/api/useFetchStructureData";
import { Loader } from "src/Components/Loader/Loader";
import defaultStructureImage from "src/Pages/Company/OrganizationStructure/assets/icons/DefaultOrgchartManagerIcon.svg";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructureStructureDelete,
  organizationStructureStructureEdit,
  organizationStructureStructureEnable,
} from "src/utils/RoleBasedSecurity/permissions";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import { RemoveStructureDialog } from "./RemoveStructure/RemoveStructureDialog";
import { styles } from "./structureSettingsStyle";

interface IStructureSettings {
  structureId: string;
}

const StructureSettings: FC<IStructureSettings> = ({ structureId }) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const theme = useTheme();
  const hasPermissionStructureEdit = usePermission(
    organizationStructureStructureEdit
  );
  const hasPermissionStructureEnable = usePermission(
    organizationStructureStructureEnable
  );
  const hasPermissionsStructureDelete = usePermission(
    organizationStructureStructureDelete
  );

  // TODO Remove the flag after confirming the structure removal operation.
  const ENABLE_STRUCTURE_REMOVAL = false;
  const companyId = useCompanyId();

  const { data: structureData, isRefetching: isRefetchingStructureData } =
    useFetchStructureData(companyId, structureId!);

  const {
    mutate: changeVisibilityMutation,
    isPending: isPendingChangeVisibility,
    error: changeVisibilityError,
  } = useChangeVisibility(companyId, structureId);

  const logStatus = useLogStatus();

  useEffect(() => {
    if (!changeVisibilityError) return;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "StructureSettings: changeVisibility failed",
      statusDetails: {
        source: "API",
        operation: "changeVisibility",
        status: (changeVisibilityError as AxiosError)?.response?.status,
        url: (changeVisibilityError as AxiosError)?.config?.url,
      },
      errorObject:
        changeVisibilityError instanceof Error
          ? changeVisibilityError
          : new Error(String(changeVisibilityError)),
    });
  }, [changeVisibilityError, logStatus]);

  const handleChangeVisibility = (switchState: { visibility: boolean }) => {
    if (structureId) {
      changeVisibilityMutation(switchState);
    }
  };

  if (!structureId || !structureData) {
    return <Loader />;
  }

  return (
    <>
      <Paper
        sx={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            color: theme.palette.info.dark,
            fontWeight: "fontWeightBold",
          }}
        >
          {__("Ustawienia ogólne:")}
        </Typography>
        {hasPermissionStructureEnable && (
          <>
            <Box>
              <Typography
                sx={{ fontSize: "16px", fontWeight: "fontWeightMedium" }}
              >
                {__("Widoczność struktury dla pracowników")}
              </Typography>
              <Typography sx={{ fontSize: "14px" }}>
                {__(
                  "Wyłączenie widoczności struktury spowoduje ukrycie jej dla pracowników"
                )}
              </Typography>
            </Box>
            <Box sx={styles.switchContainer}>
              <Typography sx={styles.switchLabel}>
                {structureData.visible
                  ? __("Struktura widoczna")
                  : __("Struktura niewidoczna")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={structureData.visible}
                      onChange={() =>
                        handleChangeVisibility({
                          visibility: !structureData.visible,
                        })
                      }
                      disabled={
                        isRefetchingStructureData || isPendingChangeVisibility
                      }
                      data-testid="os__settings-visibility-toggle"
                    />
                  }
                  label=""
                />
              </Box>
            </Box>
          </>
        )}
        <Divider sx={{ m: 0 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: theme.palette.info.dark,
              fontWeight: "fontWeightBold",
            }}
          >
            {__("Ustawienia struktury:")}
          </Typography>
          {hasPermissionStructureEdit && !isEditMode && (
            <Button
              variant="contained"
              size="large"
              onClick={() => setIsEditMode(true)}
              data-testid="os__settings-edit-button"
            >
              {__("Edytuj")}
            </Button>
          )}
        </Box>

        {isEditMode ? (
          <EditStructureForm
            disableEditMode={() => setIsEditMode(false)}
            currentAvatar={structureData.avatarLink}
            structureId={structureId!}
            structureName={structureData.name}
          />
        ) : (
          <>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "fontWeightMedium" }}
            >
              {__("Nazwa struktury:")}
            </Typography>
            <Box>
              <Typography sx={styles.structureNameText}>
                {structureData.name}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: "16px", fontWeight: "fontWeightMedium" }}
            >
              {__("Awatar struktury:")}
            </Typography>
            <Avatar
              src={structureData.avatarLink || defaultStructureImage}
              alt="avatar"
              sx={
                structureData.avatarLink ? styles.avatar : styles.defaultAvatar
              }
            />
          </>
        )}
      </Paper>

      {ENABLE_STRUCTURE_REMOVAL && hasPermissionsStructureDelete && (
        <>
          <Paper sx={styles.formWrapper}>
            <Typography
              sx={{
                fontSize: "16px",
                color: theme.palette.info.dark,
                fontWeight: "fontWeightBold",
              }}
            >
              {__("Usuwanie struktury")}:
            </Typography>
            <Button
              sx={styles.deleteButton}
              onClick={() => setIsDialogOpen(true)}
            >
              {__("Usuń strukturę")}
            </Button>
          </Paper>
          <RemoveStructureDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            structureName={structureData.name}
          />
        </>
      )}
    </>
  );
};

export default StructureSettings;
